import { Badge } from '@solinkcorp/solink-ui/components/Badge';
import { ALL_LOCATIONS } from '@solinkcorp/vd-common';
import React, { useMemo } from 'react';
import useLocale from 'src/hooks/useLocale';
import CommonUtil from 'utils/CommonUtil';
import LocaleUtil from 'utils/LocaleUtil';
import { type VideoDiscoverySite } from 'utils/types';
import { getLocationInfoToDisplay } from '../locationSelectorUtils';

interface SelectedLocationsProps {
  locationsList: readonly VideoDiscoverySite[];
  locationIds?: readonly string[];
  locationTags?: readonly string[];
  loading?: boolean;
  noLocationAccess?: boolean;
  maxCharacterLimit: number;
  tagClassName?: string;
}

const SelectedLocations: React.FC<SelectedLocationsProps> = (props) => {
  const {
    locationIds,
    locationTags,
    locationsList,
    loading = false,
    noLocationAccess = false,
    maxCharacterLimit,
    tagClassName,
  } = props;
  const locale = useLocale();

  const processedLocationNames = useMemo(() => {
    if (loading) {
      return locale.general.loading;
    }

    if (noLocationAccess) {
      return locale.general.noLocations;
    }

    if (locationIds) {
      if (locationIds.length > 1) {
        return `${locationIds.length} ${locale.general.locations}`;
      }
      const selectedLocation = locationIds[0]
        ? locationsList.find((location) => location.id === locationIds[0])
        : locationsList.find((location) => location.id === ALL_LOCATIONS);

      return selectedLocation?.name ?? `${locationIds[0]}`;
    }

    return '';
  }, [loading, noLocationAccess, locationIds, locale, locationsList]);

  const sortedLocationTags = [...(locationTags || [])].sort(
    CommonUtil.sortAlphabeticallyAndNumerically,
  );

  const truncatedLocationTagsString = `${LocaleUtil.getLocalizedStr({
    string:
      sortedLocationTags.length > 1
        ? locale.locationTagging.xLocationTags
        : locale.locationTagging.xLocationTag,
    replacements: { count: sortedLocationTags.length },
  })}`;
  const { locationTagsToDisplay, locationsToDisplay } = getLocationInfoToDisplay({
    locationNames: processedLocationNames,
    locationTags: sortedLocationTags,
    maxCharacterLimit,
    truncatedLocationTagsString,
  });

  const tagError = !!tagClassName && tagClassName.includes('error');

  return (
    <div className="tw inline-flex min-w-0 items-center gap-1">
      <span className="tw min-w-0 truncate text-foreground-primary body-md">
        {locationsToDisplay}
      </span>
      {!!locationTagsToDisplay?.length && !loading && (
        <span className="tw inline-flex min-w-0 flex-wrap gap-1">
          {locationTagsToDisplay.map((tag) => (
            <Badge key={tag} variant={tagError ? 'destructive' : 'outline'} size="compact">
              {tag}
            </Badge>
          ))}
        </span>
      )}
    </div>
  );
};

export default SelectedLocations;
